import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useIntl } from 'react-intl';

import { useListWorkingHoursExceptions } from 'src/features/workingHoursExceptions/api/useListWorkingHoursExceptions';
import CreateWorkingHoursExceptionsFormModal from 'src/features/workingHoursExceptions/components/form/CreateWorkingHoursExceptionsFormModal';
import CellWorkingHourExceptionActions from 'src/features/workingHoursExceptions/components/table/CellWorkingHourExceptionActions';
import { WorkingHoursException } from 'src/features/workingHoursExceptions/utils/types';
import { workingHoursMessages } from 'src/features/workingHoursExceptions/utils/messages';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTableControls } from 'src/registry/components/table/DataTableControls';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import useFilters from 'src/registry/hooks/useFilters';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import CellDate from 'src/registry/components/table/cells/CellDate';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

dayjs.extend(customParseFormat);

const sortingOptions = {
    initialSortingState: [
        { id: 'day_of_week', desc: false },
        { id: 'start_time', desc: false },
    ],
};
const searchOptions = { globalFilterKey: 'name' };

const columnHelper = createColumnHelper<WorkingHoursException>();
type WorkingHoursExceptionsProps = {
    siteId: string;
};

function WorkingHoursExceptions({ siteId }: WorkingHoursExceptionsProps) {
    const { formatMessage, locale } = useIntl();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { parkingId } = useParking();

    const manageSites = useHasPermissions([PERMISSION.MANAGE_SITES]);

    const payload = React.useMemo(
        () => ({
            siteId,
            parkingId,
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
        }),
        [siteId, parkingId, queryFiltersOptions, querySearchOptions, querySortingOptions],
    );

    const { data, isLoading } = useListWorkingHoursExceptions(payload);

    const columns = React.useMemo(
        () => [
            columnHelper.accessor('zone', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) =>
                    row.original.zone.translations.find((translation) => translation.language === locale)?.name,
                meta: {
                    title: formatMessage(workingHoursMessages.zone),
                },
            }),
            columnHelper.accessor('date', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: CellDate,
                meta: {
                    title: formatMessage(workingHoursMessages.date),
                },
            }),
            columnHelper.accessor('is_open', {
                header: DataTableColumnHeader,
                cell: CellBoolean,
                enableSorting: true,
                meta: {
                    title: formatMessage(workingHoursMessages.isOpen),
                },
            }),
            columnHelper.accessor('all_day', {
                header: DataTableColumnHeader,
                cell: CellBoolean,
                enableSorting: true,
                meta: {
                    title: formatMessage(workingHoursMessages.allDay),
                },
            }),
            columnHelper.accessor('start_time', {
                header: DataTableColumnHeader,
                enableSorting: true,
                meta: {
                    title: formatMessage(workingHoursMessages.startTime),
                },
            }),
            columnHelper.accessor('end_time', {
                header: DataTableColumnHeader,
                enableSorting: true,
                meta: {
                    title: formatMessage(workingHoursMessages.endTime),
                },
            }),
            columnHelper.accessor('description', {
                header: DataTableColumnHeader,
                enableSorting: true,
                meta: {
                    title: formatMessage(workingHoursMessages.description),
                },
            }),
            ...(manageSites
                ? [
                      columnHelper.display({
                          id: 'actions',
                          header: DataTableColumnHeader,
                          cell: CellWorkingHourExceptionActions,
                      }),
                  ]
                : []),
        ],
        [formatMessage, locale, manageSites],
    );

    return (
        <DataTable<WorkingHoursException, any>
            isLoading={isLoading}
            columns={columns}
            data={data}
            search={search}
            filters={filters}
            sorting={sorting}
            semantics={{
                title: formatMessage(workingHoursMessages.workingHoursExceptions),
                entryName: formatMessage(workingHoursMessages.workingHoursException).toLowerCase(),
                entryNames: formatMessage(workingHoursMessages.workingHoursExceptions).toLowerCase(),
                placeholder: formatMessage(workingHoursMessages.workingHoursExceptions),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageSites && { newEntry: CreateWorkingHoursExceptionsFormModal }),
            }}
            enableSearchParams={false}
        />
    );
}

export default WorkingHoursExceptions;
